import '@efdigital/ll-addon-coaching/styles/coach-card.css';

import { formatCoachingTime } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/format-coaching-time';
import type { CoachingPlan } from '@efdigital/ll-addon-data';
import type AdminSessionsService from '@efdigital/ll-admin-v2/services/admin-sessions';
import type { LookupUser } from '@efdigital/ll-admin-v2/services/user-lookup';
import { userDisplayName, userEmail } from '@efdigital/ll-admin-v2/utils/users';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Await } from '@warp-drive/ember';

interface CoacheeCardSignature {
  Args: {
    coachee: LookupUser;
    coachingPlan: CoachingPlan;
  };
  Element: HTMLDivElement;
}

export class CoacheeCard extends Component<CoacheeCardSignature> {
  @service declare adminSessions: AdminSessionsService;

  get name(): string {
    return userDisplayName(this.args.coachee);
  }

  get email(): string {
    return userEmail(this.args.coachee);
  }

  get initials(): string {
    const parts = this.name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const first = parts[0]?.[0] ?? '';
      const last = parts[parts.length - 1]?.[0] ?? '';
      return (first + last).toUpperCase();
    }

    return this.name.slice(0, 2).toUpperCase();
  }

  loadQuota = () => {
    const planId = this.args.coachingPlan.id;
    if (!planId) {
      return Promise.reject(new Error('Missing coaching plan id'));
    }

    return this.adminSessions.getQuota(planId);
  };

  <template>
    <div
      class="card h-100 rounded-4 border-0 shadow-sm overflow-hidden d-flex flex-column"
      data-test-coachee-card
      ...attributes
    >
      <div class="text-bg-primary p-3 d-flex align-items-start">
        <span
          class="d-inline-flex align-items-center justify-content-center flex-shrink-0 overflow-hidden rounded-circle ll-coach-avatar-sm"
          aria-hidden="true"
        >
          <svg viewBox="0 0 60 60" class="w-100 h-100 text-primary">
            <circle cx="30" cy="30" r="30" fill="var(--bs-white)" />
            <text
              x="30"
              y="30"
              text-anchor="middle"
              dominant-baseline="central"
              fill="currentColor"
              font-size="20"
              font-weight="700"
            >{{this.initials}}</text>
          </svg>
        </span>
      </div>

      <div class="card-body p-3 d-flex flex-column flex-grow-1 gap-3">
        <span
          class="h6 fw-bold d-block mb-0"
          data-test-coachee-name
        >{{this.name}}</span>

        {{#if this.email}}
          <p
            class="small text-body-secondary mb-0 d-flex align-items-center gap-1 flex-wrap"
            data-test-coachee-email
          >
            <iconify-icon inline icon="ph:envelope" aria-hidden="true" />
            {{this.email}}
          </p>
        {{/if}}

        <hr class="my-0 opacity-25 w-100" />

        <Await @promise={{(this.loadQuota)}}>
          <:success as |quota|>
            <div class="d-flex flex-column gap-2">
              <div>
                <p class="gud-eyebrow mb-1 text-body-secondary">Coaching time</p>
                <p
                  class="mb-0 fw-semibold small
                    {{unless quota.coaching.remaining 'text-danger'}}"
                  data-test-coachee-quota-coaching
                >
                  {{formatCoachingTime quota.coaching.remaining}}
                  of
                  {{formatCoachingTime quota.coaching.allocated}}
                  remaining
                </p>
              </div>
              <div>
                <p class="gud-eyebrow mb-1 text-body-secondary">Chemistry
                  sessions</p>
                <p
                  class="mb-0 fw-semibold small
                    {{unless quota.chemistry.remaining 'text-danger'}}"
                  data-test-coachee-quota-chemistry
                >
                  {{quota.chemistry.remaining}}
                  of
                  {{quota.chemistry.allocated}}
                  remaining
                </p>
              </div>
            </div>
          </:success>
          <:pending>
            <p class="small text-body-secondary mb-0">Loading quota…</p>
          </:pending>
          <:error>
            <p class="small text-danger mb-0">Unable to load quota</p>
          </:error>
        </Await>
      </div>
    </div>
  </template>
}
